import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Prompt, useHistory } from 'react-router-dom';
import { Action, Location } from 'history';
import { Table } from '@bb-ui/react-library/dist/components/Table';
import { TableBody } from '@bb-ui/react-library/dist/components/TableBody';
import { TableCell } from '@bb-ui/react-library/dist/components/TableCell';
import { TableHead } from '@bb-ui/react-library/dist/components/TableHead';
import { TableRow } from '@bb-ui/react-library/dist/components/TableRow';
import {
    DefaultButton,
    PrimaryButton,
} from '@bb-ui/react-library/dist/components/Button';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { ButtonBase } from '@bb-ui/react-library/dist/components';
import { Refresh, Test, Trash } from '@bb-ui/icons/dist/small';
import { Edit } from '@bb-ui/icons/dist/small/Edit';
import { Dialog } from '@bb-ui/react-library/dist/components/Dialog';
import { DialogTitle } from '@bb-ui/react-library/dist/components/DialogTitle';
import { DialogContent } from '@bb-ui/react-library/dist/components/DialogContent';
import { DialogContentText } from '@bb-ui/react-library/dist/components/DialogContentText';
import { DialogActions } from '@bb-ui/react-library/dist/components/DialogActions';
import { CircularProgress } from '@bb-ui/react-library/dist/components/Progress';
import { Banner } from '@bb-ui/react-library/dist/components/Banner';
import { Tooltip } from '@bb-ui/react-library/dist/components/Tooltip';
import OffScreenAnnouncement from '@bb-ui/react-library/dist/components/OffScreenAnnouncement/OffScreenAnnouncement';
import { useSharedStyles } from '../../../../utilities/sharedStyles';
import { useIdentityProviderContext } from '../../../../contexts/IdentityProviderContext';
import { useDialogTabWrap } from '../../../../hooks/useDialogTabWrap';
import { useStyles } from '../Authentication.styles';
import { useDeleteIdentityProvider } from '../hooks/useDeleteIdentityProvider';
import { useAddIdentityProvider } from '../hooks/useAddIdentityProvider';
import {
    DeleteState,
    ProvisioningState,
} from '../identityProviderManagement.types';
import {
    deriveIdentityProviderRows,
    IdentityProviderRow,
    isListed,
    RowStatus,
    takenNames,
} from '../identityProviderRows';
import { AddIdentityProviderWizard } from '../AddIdentityProviderWizard/AddIdentityProviderWizard';
import { TestIdentityProviderDialog } from '../TestIdentityProviderDialog/TestIdentityProviderDialog';
import { EditIdentityProviderDialog } from '../EditIdentityProviderDialog/EditIdentityProviderDialog';
import { SkeletonTable } from '../../../Skeleton/SkeletonTable';

const BLOCKED_KEY = {
    HAS_USERS: 'blockedHasUsers',
    LAST_IDP: 'blockedLastIdp',
    OWN_IDP: 'blockedOwnIdp',
    CHECK_UNAVAILABLE: 'blockedCheckUnavailable',
} as const;

const CONFIRM_BODY_KEY: Partial<Record<DeleteState, string>> = {
    [DeleteState.CHECKING]: 'checking',
    [DeleteState.CONFIRMABLE]: 'confirmBody',
    [DeleteState.DELETING]: 'confirmBody',
    [DeleteState.DELETE_REFUSED]: 'refused',
};

const BLOCKED_TITLE_KEY = {
    HAS_USERS: 'blockedTitleHasUsers',
    LAST_IDP: 'blockedTitleLastIdp',
    OWN_IDP: 'blockedTitleOwnIdp',
    CHECK_UNAVAILABLE: 'blockedTitleCheckUnavailable',
} as const;

/**
 * Built-in providers ship with the tenant rather than being configured, so edit,
 * test and delete do not apply. Checked on the type, which is what the
 * identity-provider service actually returns, rather than a flag of our own.
 */
const isBuiltIn = (type?: string) => type === 'LearnConnector';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

const reason = (
    t: Translate,
    action: string,
    kind: string,
    name: string,
): InertReason => ({
    label: t(`settings.authentication.list.${action}${kind}Aria`, { name }),
    tooltip: t(`settings.authentication.list.${action}${kind}Tooltip`),
});

/** Test and delete are inert for one reason only. */
const inertBuiltIn = (
    row: IdentityProviderRow,
    action: 'test' | 'delete',
    t: Translate,
): InertReason | undefined =>
    isBuiltIn(row.type)
        ? reason(t, action, 'BuiltIn', row.displayName)
        : undefined;

const inertEdit = (
    row: IdentityProviderRow,
    t: Translate,
): InertReason | undefined => {
    const kind = (
        [
            ['BuiltIn', isBuiltIn(row.type)],
            ['Cloned', row.cloned],
        ] as const
    ).find(([, applies]) => applies)?.[0];
    return kind && reason(t, 'edit', kind, row.displayName);
};

interface InertReason {
    label: string;
    tooltip: string;
}

interface ActionButtonProps {
    inert?: InertReason;
    label: string;
    tooltip: string;
    onAction: () => void;
    testId: string;
    className?: string;
    buttonRef?: (el: HTMLButtonElement) => void;
}

/**
 * One row action, inert when it does not apply.
 *
 * Inert via `aria-disabled` and a swallowed click, not `disabled`: a disabled
 * button leaves the tab order and takes its tooltip with it, hiding the reason
 * from exactly the users who cannot see the greyed-out styling.
 *
 * The icons carry no visible text, so every action has a tooltip naming it; an
 * inert one's says why the action is unavailable instead.
 */
const ActionButton: React.FunctionComponent<ActionButtonProps> = ({
    inert,
    label,
    tooltip,
    onAction,
    testId,
    className,
    buttonRef,
    children,
}) => {
    const classes = useStyles();
    const button = (
        <ButtonBase
            className={classnames(className, {
                [classes.actionIconWrapperBuiltIn]: !!inert,
            })}
            ref={buttonRef}
            aria-label={inert?.label ?? label}
            aria-disabled={!!inert || undefined}
            onClick={inert ? undefined : onAction}
            data-testid={testId}
        >
            {children}
        </ButtonBase>
    );

    return <Tooltip title={inert?.tooltip ?? tooltip}>{button}</Tooltip>;
};

/**
 * Finished add attempts that may have created a provider: provisioned, or timed
 * out (we stopped waiting, which does not mean it failed).
 */
const MAY_HAVE_CREATED: ReadonlySet<ProvisioningState> = new Set([
    ProvisioningState.PROVISIONED,
    ProvisioningState.TIMED_OUT,
]);

/** The add wizard, while open. */
interface WizardState {
    /** Key of the add attempt the wizard shows. */
    shownKey?: string;
    /**
     * Key of the failed add attempt being retried. Its input prefills the
     * form.
     */
    retryKey?: string;
    /**
     * Opened on the add attempt in flight, because only one add attempt runs
     * at a time.
     */
    openedOnAddInFlight?: boolean;
}

/**
 * The Authentication tab's main surface: the provider list, plus the add and
 * delete actions.
 * One component, because delete is a modal launched from row context.
 */
interface IdentityProviderManagerProps {
    /** Called after a change the service records in the audit log. */
    onMutated?: () => void;
}

export const IdentityProviderManager: React.FunctionComponent<
    IdentityProviderManagerProps
> = ({ onMutated }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const sharedClasses = useSharedStyles();
    const { idps, error, refetch } = useIdentityProviderContext();
    const del = useDeleteIdentityProvider();
    useDialogTabWrap(
        ![DeleteState.IDLE, DeleteState.DELETED].includes(del.state),
    );

    /** The provider whose test dialog is open. */
    const [testingIdp, setTestingIdp] = React.useState<{
        displayName: string;
        connectionName?: string;
    }>();
    /** Id of the provider being edited, if any. */
    const [editingIdpId, setEditingIdpId] = React.useState<string>();
    const [wizard, setWizard] = React.useState<WizardState>();

    const headingRef = React.useRef<HTMLHeadingElement>(null);
    const addButtonRef = React.useRef<HTMLButtonElement>(null);
    /** The Delete button of the row currently being acted on, for focus restore. */
    const rowButtonRefs = React.useRef<
        Record<string, HTMLButtonElement | null>
    >({});

    const refresh = React.useCallback(() => {
        refetch();
        onMutated?.();
    }, [refetch, onMutated]);

    /**
     * Every add attempt made on this page. A new provider has no row actions
     * until the list includes it, so fetch the list again once it is
     * provisioned.
     */
    const adds = useAddIdentityProvider({ onProvisioned: refresh });
    const { attempts, remove: removeAdds } = adds;

    const rows: IdentityProviderRow[] = React.useMemo(
        () =>
            deriveIdentityProviderRows({
                idps: (idps ?? []).map(i => ({ ...i })),
                attempts,
            }),
        [idps, attempts],
    );

    // Forget finished add attempts the list now includes. Their row has nothing
    // more to show, and it must not come back if that provider is later
    // deleted.
    React.useEffect(() => {
        const listed = attempts
            .filter(
                a => MAY_HAVE_CREATED.has(a.state) && isListed(idps ?? [], a),
            )
            .map(a => a.key);
        if (listed.length) removeAdds(listed);
    }, [attempts, idps, removeAdds]);

    const shownAttempt = attempts.find(a => a.key === wizard?.shownKey);
    const retriedAttempt = attempts.find(a => a.key === wizard?.retryKey);
    const editing = rows.find(r => r.id === editingIdpId);

    /**
     * Names the add wizard refuses. A retried add attempt keeps its own name;
     * otherwise retrying a timed-out add attempt would be refused.
     */
    const retryKey = wizard?.retryKey;
    const namesTakenForAdd = React.useMemo(
        () =>
            takenNames(
                rows.filter(r => !r.attemptKey || r.attemptKey !== retryKey),
            ),
        [rows, retryKey],
    );
    const namesTakenForEdit = React.useMemo(
        () => takenNames(rows, editing?.displayName),
        [rows, editing?.displayName],
    );

    const closeDelete = React.useCallback(
        (deleted: boolean) => {
            const id = del.idpId;
            del.reset();
            if (deleted) {
                headingRef.current?.focus();
            } else if (id) {
                rowButtonRefs.current[id]?.focus();
            }
        },
        [del],
    );

    React.useEffect(() => {
        if (
            del.state === DeleteState.DELETED ||
            del.state === DeleteState.ALREADY_DELETED
        ) {
            closeDelete(true);
            refresh();
        }
    }, [del.state, closeDelete, refresh]);

    /** Leaving while an add attempt runs would hide its outcome, so the browser and the app ask first. */
    const provisioning = !!adds.inFlight;
    const history = useHistory();
    const [leavingTo, setLeavingTo] = React.useState<{
        location: Location;
        action: Action;
    }>();
    /** Lets exactly one navigation through: the one the admin confirmed. */
    const leaveApproved = React.useRef(false);
    const leave = () => {
        const { location, action } = leavingTo!;
        leaveApproved.current = true;
        // Back and Forward cannot be replayed exactly; Back is the usual one
        const resume = {
            POP: () => history.goBack(),
            PUSH: () => history.push(location),
            REPLACE: () => history.replace(location),
        };
        resume[action]();
        setLeavingTo(undefined);
    };
    React.useEffect(() => {
        if (!provisioning) {
            // Finished provisioning closes the leave dialog, so the user sees the result
            setLeavingTo(undefined);
            return undefined;
        }
        const confirmLeave = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = ''; // Chrome and Edge before 119
        };
        window.addEventListener('beforeunload', confirmLeave);
        return () => window.removeEventListener('beforeunload', confirmLeave);
    }, [provisioning]);

    /** The provider the current dialog is about, for the copy that names it. */
    const blockedName = rows.find(r => r.id === del.idpId)?.displayName ?? '';

    /**
     * Open the add wizard, empty or to retry a failed add attempt.
     *
     * Only one add attempt runs at a time. While one is in flight, the wizard
     * opens on it instead and says why.
     */
    const openWizard = (retryKey?: string) =>
        setWizard(
            adds.inFlight
                ? { shownKey: adds.inFlight.key, openedOnAddInFlight: true }
                : { retryKey },
        );

    /** "Add identity provider" on the last-provider dialog. */
    const addFromBlocked = () => {
        del.reset();
        openWizard();
    };

    /**
     * What is happening to this row, when something is. Provisioning and failure
     * are shown in the actions cell, which is empty until the row settles and its
     * actions appear.
     */
    const provisioningNote = (row: IdentityProviderRow) => {
        if (row.status === RowStatus.PROVISIONING) {
            return (
                <>
                    <CircularProgress
                        size={16}
                        ariaLabel={t(
                            'settings.authentication.list.provisioningBusy',
                        )}
                    />
                    {t('settings.authentication.list.statusProvisioning')}
                </>
            );
        }
        if (row.status === RowStatus.FAILED) {
            const reason = t(
                row.failureReason === 'TIMED_OUT'
                    ? 'settings.authentication.list.statusFailedTimedOut'
                    : 'settings.authentication.list.statusFailedRefused',
            );
            return (
                <span className={classes.statusFailedCell}>
                    <span className={classes.statusFailed}>
                        {t('settings.authentication.list.statusFailed')}
                    </span>
                    <span
                        className={classes.statusFailedReason}
                        data-testid={`idpFailureReason-${row.id}`}
                    >
                        {reason}
                    </span>
                </span>
            );
        }
        return undefined;
    };

    if (error) {
        return (
            <Banner
                severity="error"
                className={classes.banner}
                data-testid="idpListError"
            >
                {t('settings.authentication.list.loadError')}
                <DefaultButton
                    className={classes.bannerAction}
                    onClick={() => refetch()}
                    data-testid="idpListRetry"
                >
                    {t('settings.authentication.list.retry')}
                </DefaultButton>
            </Banner>
        );
    }

    return (
        <section className={classes.section}>
            <div className={classes.toolbar}>
                <Typography
                    variant="h2"
                    ref={headingRef}
                    className={classes.sectionHeading}
                    tabIndex={-1}
                    data-testid="heading"
                >
                    {t('settings.authentication.list.heading')}
                </Typography>
                <ButtonBase
                    ref={addButtonRef}
                    className={classes.addIdentityProviderButton}
                    onClick={() => openWizard()}
                    data-testid="addIdentityProviderButton"
                >
                    <Typography component="span" variant="subtitle2">
                        {t('settings.authentication.list.add')}
                    </Typography>
                </ButtonBase>
            </div>

            <div
                aria-live="polite"
                data-testid="idpStatusLiveRegion"
                className={sharedClasses.srOnly}
            >
                {rows.find(r => r.status === RowStatus.PROVISIONING)
                    ? t('settings.authentication.list.statusProvisioning')
                    : ''}
            </div>

            {idps === undefined && (
                <div data-testid="idpListLoading">
                    <OffScreenAnnouncement
                        type="polite"
                        message={t('settings.authentication.list.loading')}
                        changeTrigger={1}
                    />
                    <SkeletonTable columnNum={3} rowNum={3} />
                </div>
            )}
            {idps !== undefined && rows.length === 0 && (
                <p className={classes.emptyState} data-testid="idpListEmpty">
                    {t('settings.authentication.list.empty')}
                </p>
            )}
            {rows.length > 0 && (
                <div className={classes.tableContainer}>
                    <Table
                        aria-label={t('settings.authentication.list.heading')}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {t('settings.authentication.list.colName')}
                                </TableCell>
                                <TableCell>
                                    {t('settings.authentication.list.colType')}
                                </TableCell>
                                <TableCell
                                    className={classes.actionsHeaderCell}
                                >
                                    {t(
                                        'settings.authentication.list.colActions',
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow
                                    key={row.id}
                                    data-testid={`idpRow-${row.id}`}
                                >
                                    <TableCell>{row.displayName}</TableCell>
                                    <TableCell>{row.type ?? 'SAML'}</TableCell>
                                    <TableCell>
                                        <div
                                            className={classes.actionsContainer}
                                        >
                                            {provisioningNote(row) && (
                                                <span
                                                    className={
                                                        classes.statusCell
                                                    }
                                                >
                                                    {provisioningNote(row)}
                                                </span>
                                            )}

                                            {row.pending &&
                                                row.status ===
                                                    RowStatus.FAILED && (
                                                    <div
                                                        className={
                                                            classes.actionContainer
                                                        }
                                                    >
                                                        <ButtonBase
                                                            className={
                                                                classes.actionIconWrapper
                                                            }
                                                            aria-label={t(
                                                                'settings.authentication.list.retryAria',
                                                                {
                                                                    name: row.displayName,
                                                                },
                                                            )}
                                                            onClick={() =>
                                                                openWizard(
                                                                    row.attemptKey,
                                                                )
                                                            }
                                                            data-testid={`retryIdp-${row.id}`}
                                                        >
                                                            <Refresh
                                                                className={
                                                                    classes.actionIcon
                                                                }
                                                            />
                                                        </ButtonBase>
                                                    </div>
                                                )}

                                            {!row.pending && (
                                                <div
                                                    className={
                                                        classes.actionContainer
                                                    }
                                                >
                                                    <ActionButton
                                                        inert={inertEdit(
                                                            row,
                                                            t,
                                                        )}
                                                        label={t(
                                                            'settings.authentication.list.editAria',
                                                            {
                                                                name: row.displayName,
                                                            },
                                                        )}
                                                        tooltip={t<string>(
                                                            'settings.authentication.list.editTooltip',
                                                        )}
                                                        onAction={() =>
                                                            setEditingIdpId(
                                                                row.id,
                                                            )
                                                        }
                                                        testId={`editIdp-${row.id}`}
                                                        className={
                                                            classes.actionIconWrapper
                                                        }
                                                    >
                                                        <Edit
                                                            className={
                                                                classes.actionIcon
                                                            }
                                                        />
                                                    </ActionButton>
                                                </div>
                                            )}

                                            {!row.pending && (
                                                <div
                                                    className={
                                                        classes.actionContainer
                                                    }
                                                >
                                                    <ActionButton
                                                        inert={inertBuiltIn(
                                                            row,
                                                            'test',
                                                            t,
                                                        )}
                                                        label={t(
                                                            'settings.authentication.list.testAria',
                                                            {
                                                                name: row.displayName,
                                                            },
                                                        )}
                                                        tooltip={t<string>(
                                                            'settings.authentication.list.testTooltip',
                                                        )}
                                                        onAction={() =>
                                                            setTestingIdp({
                                                                displayName:
                                                                    row.displayName,
                                                                connectionName:
                                                                    row.connectionName,
                                                            })
                                                        }
                                                        testId={`testIdp-${row.id}`}
                                                        className={
                                                            classes.actionIconWrapper
                                                        }
                                                    >
                                                        <Test
                                                            className={
                                                                classes.actionIcon
                                                            }
                                                        />
                                                    </ActionButton>
                                                </div>
                                            )}
                                            {!row.pending && (
                                                <div
                                                    className={
                                                        classes.actionContainer
                                                    }
                                                >
                                                    <ActionButton
                                                        inert={inertBuiltIn(
                                                            row,
                                                            'delete',
                                                            t,
                                                        )}
                                                        className={
                                                            classes.actionIconWrapper
                                                        }
                                                        buttonRef={(
                                                            el: HTMLButtonElement,
                                                        ) => {
                                                            rowButtonRefs.current[
                                                                row.id
                                                            ] = el;
                                                        }}
                                                        label={t(
                                                            'settings.authentication.list.deleteAria',
                                                            {
                                                                name: row.displayName,
                                                            },
                                                        )}
                                                        tooltip={t<string>(
                                                            'settings.authentication.list.deleteTooltip',
                                                        )}
                                                        onAction={() =>
                                                            del.check(row.id)
                                                        }
                                                        testId={`deleteIdp-${row.id}`}
                                                    >
                                                        <Trash
                                                            className={
                                                                classes.actionIcon
                                                            }
                                                        />
                                                    </ActionButton>
                                                </div>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <Dialog
                open={del.state === DeleteState.BLOCKED}
                onClose={() => closeDelete(false)}
                maxWidth="md"
                isAlert
                aria-labelledby="idp-delete-blocked-title"
                aria-describedby="idp-delete-blocked-body"
                data-testid="deleteBlockedDialog"
            >
                <DialogTitle id="idp-delete-blocked-title" hideCloseButton>
                    {del.blockedReason &&
                        t(
                            `settings.authentication.delete.${
                                BLOCKED_TITLE_KEY[del.blockedReason]
                            }`,
                            { name: blockedName },
                        )}
                </DialogTitle>
                <DialogContent className={classes.dialogConfirmContent}>
                    <DialogContentText id="idp-delete-blocked-body">
                        {del.blockedReason &&
                            t(
                                `settings.authentication.delete.${
                                    BLOCKED_KEY[del.blockedReason]
                                }`,
                                {
                                    count: del.usersAssignedCount ?? 0,
                                    name: blockedName,
                                },
                            )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    {del.blockedReason === 'LAST_IDP' && (
                        <PrimaryButton
                            onClick={addFromBlocked}
                            data-testid="blockedAddIdp"
                        >
                            {t(
                                'settings.authentication.delete.addIdentityProvider',
                            )}
                        </PrimaryButton>
                    )}
                    <DefaultButton
                        onClick={() => closeDelete(false)}
                        data-testid="blockedClose"
                    >
                        {t('settings.authentication.delete.close')}
                    </DefaultButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={
                    del.state === DeleteState.CHECKING ||
                    del.state === DeleteState.CONFIRMABLE ||
                    del.state === DeleteState.DELETING ||
                    del.state === DeleteState.DELETE_REFUSED
                }
                onClose={() => closeDelete(false)}
                maxWidth="md"
                isAlert
                aria-labelledby="idp-delete-confirm-title"
                aria-describedby="idp-delete-confirm-body"
                data-testid="deleteConfirmDialog"
            >
                <DialogTitle id="idp-delete-confirm-title" hideCloseButton>
                    {t('settings.authentication.delete.confirmTitle', {
                        name: blockedName,
                    })}
                </DialogTitle>
                <DialogContent className={classes.dialogConfirmContent}>
                    <DialogContentText
                        id="idp-delete-confirm-body"
                        role="status"
                    >
                        {del.state === DeleteState.CHECKING && (
                            <span
                                aria-hidden="true"
                                className={classes.checkingSpinner}
                                data-testid="deleteCheckInProgress"
                            >
                                <CircularProgress
                                    size={18}
                                    ariaLabel={t(
                                        'settings.authentication.delete.checking',
                                    )}
                                />
                            </span>
                        )}
                        {CONFIRM_BODY_KEY[del.state] &&
                            t(
                                `settings.authentication.delete.${
                                    CONFIRM_BODY_KEY[del.state]
                                }`,
                                { name: blockedName },
                            )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <DefaultButton
                        onClick={() => closeDelete(false)}
                        data-testid="deleteCancel"
                    >
                        {t('settings.authentication.delete.cancel')}
                    </DefaultButton>
                    {del.state === DeleteState.DELETE_REFUSED && (
                        <PrimaryButton
                            onClick={() => del.idpId && del.check(del.idpId)}
                            data-testid="deleteRefusedRecheck"
                        >
                            {t('settings.authentication.delete.recheck')}
                        </PrimaryButton>
                    )}
                    {(del.state === DeleteState.CONFIRMABLE ||
                        del.state === DeleteState.DELETING) && (
                        <PrimaryButton
                            onClick={del.confirm}
                            disabled={del.state !== DeleteState.CONFIRMABLE}
                            data-testid="deleteConfirm"
                        >
                            {del.state === DeleteState.DELETING ? (
                                <CircularProgress
                                    size={18}
                                    ariaLabel={t(
                                        'settings.authentication.delete.deleting',
                                    )}
                                    data-testid="deleteInProgress"
                                />
                            ) : (
                                t('settings.authentication.delete.confirm')
                            )}
                        </PrimaryButton>
                    )}
                </DialogActions>
            </Dialog>

            <Prompt
                when={provisioning}
                message={(location, action) => {
                    if (leaveApproved.current) {
                        leaveApproved.current = false;
                        return true;
                    }
                    setLeavingTo({ location, action });
                    return false;
                }}
            />
            <Dialog
                open={!!leavingTo}
                onClose={() => setLeavingTo(undefined)}
                maxWidth="md"
                isAlert
                aria-labelledby="idp-leave-title"
                aria-describedby="idp-leave-body"
            >
                <DialogTitle id="idp-leave-title" hideCloseButton>
                    {t('settings.authentication.leave.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="idp-leave-body">
                        {t('settings.authentication.leave.body')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <DefaultButton onClick={leave}>
                        {t('settings.authentication.leave.leave')}
                    </DefaultButton>
                    <PrimaryButton onClick={() => setLeavingTo(undefined)}>
                        {t('settings.authentication.leave.stay')}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>

            <EditIdentityProviderDialog
                open={editingIdpId !== undefined}
                onClose={() => setEditingIdpId(undefined)}
                identityProvider={editing}
                onSaved={refresh}
                takenNames={namesTakenForEdit}
            />

            <TestIdentityProviderDialog
                open={testingIdp !== undefined}
                onClose={() => setTestingIdp(undefined)}
                identityProviderName={testingIdp?.displayName}
                connectionName={testingIdp?.connectionName}
            />

            <AddIdentityProviderWizard
                open={!!wizard}
                onClose={() => setWizard(undefined)}
                attempt={shownAttempt}
                onSubmit={input => {
                    const shownKey = adds.submit(input);
                    // A new add attempt, not the one the wizard was opened on.
                    setWizard(w => ({
                        ...w,
                        shownKey,
                        openedOnAddInFlight: false,
                    }));
                }}
                onRelease={() =>
                    setWizard(w => ({ ...w, shownKey: undefined }))
                }
                onTest={target => {
                    setWizard(undefined);
                    setTestingIdp(target);
                }}
                initialValues={retriedAttempt?.input}
                openedOnAddInFlight={wizard?.openedOnAddInFlight}
                takenNames={namesTakenForAdd}
            />
        </section>
    );
};
